import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
  ContactAddressDto,
  ContactDto,
  ContactGroupMemberDto,
  ContactHolidayDto,
  ContactsOptions,
  ContactTypeDto,
  FrenchAPIPostCodeDTO,
  LowInformationContactDto,
  LowInformationsDTO,
  LowInformationsParams,
  ManagementPostalCodesDTO,
  RechercheContactsParams,
  SearchContactGroupMembersParams,
} from '../interfaces';
import { sortBy } from 'underscore';
import { ContactsRemplacantDTO } from 'src/app/shared/models/iContact/contact/remplacant';
import { RechercheContacts, RechercheContactsDTO } from '../interfaces/contacts/dtos/recherche-contacts-dto.interface';
import { PostalCodeByCountryParams } from '../interfaces/contacts/params/postal-code-by-country-params.interface';
import { PostalCodeByCountryDTO } from '../interfaces/contacts/dtos/postal-code-by-country-dto.interface';
import { StreetsByCountryParams } from '../interfaces/contacts/params/streets-by-country-params.interface';
import { StreetsByCountryDTO } from '../interfaces/contacts/dtos/streets-by-country-dto.interface';
import { PostalCodesStreetsParams } from '../interfaces/contacts/params/postal-codes-streets-params.interface';
import { PostalCodesStreetsDTO } from '../interfaces/contacts/dtos/postal-codes-streets-dto.interface';
import { FrenchAPIStreetsDTO, ManagementStreetsDTO } from '../interfaces/addresses/dtos/streets-dto.interface';
import { ContactAddressDTOParams } from 'src/app/shared/models/iContact/contact/contact-adresse';
import { ContactPutParams, PostContactParams } from 'src/app/shared/models/iContact/contact/contacts-params';
import { environment } from 'src/environments/environment';
import { PostPostalCodeParams } from 'src/app/shared/models/postal-codes/post-postal-code.';
import { ManagementStreetsParams } from '../models';
import { ContactTypeParams } from '../models/iContact/contact/contact-type';

@Injectable({
  providedIn: 'root',
})
export class ContactsService {
  private iAdminURL: string;
  private iContactAdmin: string;
  private iContactiCourrier: string;
  public stopRequestAutocompleteSender: Subject<void> = new Subject<void>();

  constructor(private http: HttpClient) {
    const config = environment;
    this.iAdminURL = config.iAdminApiBaseURL;
    this.iContactAdmin = config.apiContactAdminBaseUrl;
    this.iContactiCourrier = config.apiContactiCourrierV1BaseUrl;
  }

  // #region lowinformations
  getLowInformationsContactsByApp(appName: 'iCourrier' | 'iAdmin', data?: LowInformationsParams): Observable<LowInformationsDTO[]> {
    switch (appName) {
      case 'iAdmin':
        return this.http.post<LowInformationsDTO[]>(`${this.iContactAdmin}/contacts/lowinformation`, data);
      case 'iCourrier':
        return this.http.post<LowInformationsDTO[]>(`${this.iContactiCourrier}/contacts/lowinformation`, data);
    }
  }

  public fetchContactsSummary(from: 'iAdmin' | 'iCourrier'): Observable<LowInformationContactDto[]> {
    switch (from) {
      case 'iAdmin':
        return this.http.post<LowInformationContactDto[]>(`${this.iContactAdmin}/contacts/lowinformation`, {});

      case 'iCourrier':
        return this.http.post<LowInformationContactDto[]>(`${this.iContactiCourrier}/contacts/lowinformation`, {});
    }
  }

  getLowInformationsContactsByAppSubject(appName: 'iCourrier' | 'iAdmin', data?: LowInformationsParams): Observable<LowInformationsDTO[]> {
    switch (appName) {
      case 'iAdmin':
        return this.http
          .post<LowInformationsDTO[]>(`${this.iContactAdmin}/contacts/lowinformation`, data)
          .pipe(takeUntil(this.stopRequestAutocompleteSender));
      case 'iCourrier':
        return this.http
          .post<LowInformationsDTO[]>(`${this.iContactiCourrier}/contacts/lowinformation`, data)
          .pipe(takeUntil(this.stopRequestAutocompleteSender));
    }
  }

  getLowInformationsContactsiCourrier(data?: LowInformationsParams): Observable<LowInformationsDTO[]> {
    if (!data) {
      data = {};
    }
    return this.http.post<LowInformationsDTO[]>(`${this.iContactiCourrier}/contacts/lowinformation`, data);
  }
  getLowInformationsContactsiAdmin(data?: LowInformationsParams): Observable<LowInformationsDTO[]> {
    return this.http.post<LowInformationsDTO[]>(`${this.iContactAdmin}/contacts/lowinformation`, data);
  }
  //#endregion

  //#region contacts
  public fetchContact(contactId: number, from: 'iAdmin' | 'iCourrier'): Observable<ContactDto> {
    switch (from) {
      case 'iAdmin':
        return this.http.get<ContactDto>(`${this.iContactAdmin}/contacts/${contactId}`).pipe(
          map((contact: ContactDto) => {
            contact.firstname = contact.prenom;
            contact.lastname = contact.nom;
            return contact;
          })
        );
      case 'iCourrier':
        return this.http.get<ContactDto>(`${this.iContactiCourrier}/contacts/${contactId}`).pipe(
          map((contact: ContactDto) => {
            contact.firstname = contact.prenom;
            contact.lastname = contact.nom;
            return contact;
          })
        );
    }
  }

  public fetchContacts(from: 'iAdmin' | 'iCourrier', searchContactsOptions?: ContactsOptions): Observable<ContactDto[]> {
    switch (from) {
      case 'iAdmin':
        return this.http
          .get<ContactDto[]>(`${this.iContactAdmin}/contacts`, {
            params: searchContactsOptions?.params ? { ...searchContactsOptions.params } : {},
          })
          .pipe(map((res) => (searchContactsOptions?.shouldBeSorted ? sortBy(res, 'nom') : res)));

      case 'iCourrier':
        return this.http
          .get<ContactDto[]>(`${this.iContactiCourrier}/contacts`, {
            params: searchContactsOptions?.params ? { ...searchContactsOptions.params } : {},
          })
          .pipe(map((res) => (searchContactsOptions?.shouldBeSorted ? sortBy(res, 'nom') : res)));
    }
  }

  public postContact(from: 'iAdmin' | 'iCourrier', contact: PostContactParams, skipWarnings: boolean = false): Observable<void> {
    switch (from) {
      case 'iAdmin':
        return this.http.post<void>(`${this.iContactAdmin}/contacts?skipWarnings=${skipWarnings}`, contact);
      case 'iCourrier':
        return this.http.post<void>(`${this.iContactiCourrier}/contacts?skipWarnings=${skipWarnings}`, contact);
    }
  }

  public putContact(from: 'iAdmin' | 'iCourrier', contactId: number, contact: ContactPutParams) {
    switch (from) {
      case 'iAdmin':
        return this.http.put(`${this.iContactAdmin}/contacts/${contactId}`, contact);
      case 'iCourrier':
        return this.http.put(`${this.iContactiCourrier}/contacts/${contactId}`, contact);
    }
  }

  public deleteContact(from: 'iAdmin' | 'iCourrier', contactId: number): Observable<void> {
    switch (from) {
      case 'iAdmin':
        return this.http.delete<void>(`${this.iContactAdmin}/contacts/${contactId}`);
      case 'iCourrier':
        return this.http.delete<void>(`${this.iContactiCourrier}/contacts/${contactId}`);
    }
  }

  //#endregion

  //#region Global

  getPostalCodesByCountry(countryId: number, queryParams?: PostalCodeByCountryParams): Observable<PostalCodeByCountryDTO[]> {
    if (queryParams) {
      let params = new HttpParams();
      Object.keys(queryParams).forEach((key) => {
        params = params.set(key, queryParams[key]);
      });
      return this.http.get<PostalCodeByCountryDTO[]>(`${this.iContactiCourrier}/Global/pays/${countryId}/CodesPostaux`, { params: params });
    } else {
      return this.http.get<PostalCodeByCountryDTO[]>(`${this.iContactiCourrier}/Global/pays/${countryId}/CodesPostaux`);
    }
  }

  getStreetsByCountry(countryId: number, postalCodeId: number, queryParams?: StreetsByCountryParams): Observable<StreetsByCountryDTO[]> {
    if (queryParams) {
      let params = new HttpParams();
      Object.keys(queryParams).forEach((key) => {
        params = params.set(key, queryParams[key]);
      });
      return this.http.get<StreetsByCountryDTO[]>(`$${this.iContactiCourrier}/Global/pays/${countryId}/CodesPostaux/${postalCodeId}/rues`, {
        params: params,
      });
    } else {
      return this.http.get<StreetsByCountryDTO[]>(`${this.iContactiCourrier}/Global/pays/${countryId}/CodesPostaux/${postalCodeId}/rues`);
    }
  }

  public fetchAddressTypes(): Observable<string[]> {
    return this.http.get<string[]>(`${this.iContactiCourrier}/Global/typesAdresses`);
  }

  //#endregion

  //#region french API

  getFrenchAPIPostCode(postcode: number | string): Observable<FrenchAPIPostCodeDTO> {
    return this.http.get<FrenchAPIPostCodeDTO>(`https://api-adresse.data.gouv.fr/search/?q=${postcode}&type=municipality&limit=50`);
  }

  getFrenchAPIStreet(search: string, cityCode: number): Observable<FrenchAPIStreetsDTO> {
    return this.http.get<FrenchAPIStreetsDTO>(`https://api-adresse.data.gouv.fr/search/?q=${search}&postcode=${cityCode}&type=street&limit=50`);
  }

  //#endregion

  //#region management
  public postPostalCode(postalCode: PostPostalCodeParams): Observable<ManagementPostalCodesDTO> {
    return this.http.post<ManagementPostalCodesDTO>(`${this.iContactiCourrier}/Management/CodesPostaux`, postalCode);
  }

  public postStreet(street: ManagementStreetsParams): Observable<ManagementStreetsDTO> {
    return this.http.post<ManagementStreetsDTO>(`${this.iContactiCourrier}/Management/Rues`, street);
  }

  postCodesPostauxRues(data: PostalCodesStreetsParams): Observable<PostalCodesStreetsDTO> {
    return this.http.post<PostalCodesStreetsDTO>(`${this.iContactiCourrier}/Management/CodesPostauxRues`, data);
  }

  //#endregion

  //#region contactAdresses

  public fetchContactAddresses(contactId: number): Observable<ContactAddressDto[]> {
    return this.http.get<ContactAddressDto[]>(`${this.iContactiCourrier}/contacts/${contactId}/contactAdresses`);
  }

  public putContactAddresses(contactId: number, addressId: number, addressDatas: ContactAddressDTOParams): Observable<void> {
    return this.http.put<void>(`${this.iContactiCourrier}/contacts/${contactId}/contactAdresses/${addressId}`, addressDatas);
  }

  public deleteContactAddresses(contactId: number, addressId: number): Observable<void> {
    return this.http.delete<void>(`${this.iContactiCourrier}/contacts/${contactId}/contactAdresses/${addressId}`);
  }

  //#endregion

  public fetchContactGroupMembers(searchContactGroupMembersParams?: SearchContactGroupMembersParams): Observable<ContactGroupMemberDto[]> {
    return this.http.get<ContactGroupMemberDto[]>(`${this.iAdminURL}/Search`, {
      params: searchContactGroupMembersParams ? { ...searchContactGroupMembersParams } : {},
    });
  }

  public postManagementContactType(contactType: ContactTypeParams): Observable<ContactTypeDto> {
    return this.http.post<ContactTypeDto>(`${this.iAdminURL}/Management/TypesContacts`, contactType);
  }

  public fetchHolidays(): Observable<ContactHolidayDto[]> {
    return this.http.get<ContactHolidayDto[]>(`${this.iContactiCourrier}/Management/Conges`);
  }

  public fetchSearchContacts(data: RechercheContactsParams, from: 'iAdmin' | 'iCourrier'): Observable<RechercheContacts[]> {
    switch (from) {
      case 'iAdmin':
        return this.http.post<RechercheContacts[]>(`${this.iContactAdmin}/rechercheContacts`, data).pipe(
          map((res: RechercheContactsDTO[]) => {
            const rechercheContactArrays: RechercheContacts[] = [];

            res.forEach((contact: RechercheContactsDTO) => {
              let rechercheContact: RechercheContacts = {} as RechercheContacts;
              rechercheContact = {
                ...contact,
                name: (contact.prenom ? contact.prenom : '') + ' ' + (contact.nom ? contact.nom : ''),
              };
              rechercheContactArrays.push(rechercheContact);
            });
            return rechercheContactArrays;
          })
        );
      case 'iCourrier':
        return this.http.post<RechercheContacts[]>(`${this.iContactiCourrier}/rechercheContacts`, data).pipe(
          map((res: RechercheContactsDTO[]) => {
            const rechercheContactArrays: RechercheContacts[] = [];

            res.forEach((contact: RechercheContactsDTO) => {
              let rechercheContact: RechercheContacts = {} as RechercheContacts;
              rechercheContact = {
                ...contact,
                name: (contact.prenom ? contact.prenom : '') + ' ' + (contact.nom ? contact.nom : ''),
              };
              rechercheContactArrays.push(rechercheContact);
            });
            return rechercheContactArrays;
          })
        );
    }
  }
  public fetchContactsRemplacants(contactsIds: number[]): Observable<ContactsRemplacantDTO[]> {
    return this.http.get<ContactsRemplacantDTO[]>(`${this.iContactAdmin}/contacts/remplacants?contactIds=${contactsIds}`);
  }

  public fetchContactsRemplacantsiCourrier(contactsIds: number[]): Observable<ContactsRemplacantDTO[]> {
    return this.http.get<ContactsRemplacantDTO[]>(`${this.iContactiCourrier}/contacts/remplacants?contactIds=${contactsIds}`);
  }
}
